import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Tooltip } from '@heroui/react';
import { DataGrid, type DataGridColumn } from '@heroui-pro/react';
import {
    buildDataGridSkeletonColumns,
    buildDataGridSkeletonRows,
} from '@/components';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { HoldingCheck, ShareholderRow } from '../../dividendsMeta';

type ShareholderTableProps = {
    ariaLabel: string;
    rows: ShareholderRow[];
    /** Null while the register is still loading. */
    holdings: HoldingCheck | null;
    /**
     * The register is still in flight. The table keeps its real column headers
     * and skeletons only the rows — an empty list while loading means "not known
     * yet", never "no shareholders".
     */
    isLoading?: boolean;
    onEdit: (shareholderId: string) => void;
};

const SKELETON_ROW_COUNT = 4;

/**
 * The shareholder register.
 *
 * A HeroUI Pro `DataGrid`, which renders a real `<table>` underneath — genuine
 * `columnheader`s and a `rowheader` for the name, so it still reads as a ledger
 * listing. Its accessible name is `aria-label`; DataGrid has no `<caption>` slot.
 *
 * Two per-shareholder facts get their own visible treatment rather than hiding in
 * a drawer, because both change what a distribution costs: a **withholding rate
 * that differs from the entity default** (treaty relief) is badged as an override
 * with its reason, and a **CIT-exempt** holder is marked, because their slice
 * triggers no corporate tax at all.
 *
 * When holdings do not total 100% the table **warns and keeps working**. A
 * register mid-edit legitimately does not add up, and refusing to show it until it
 * does would be worse than saying so.
 */
export const ShareholderTable: React.FC<ShareholderTableProps> = ({
    ariaLabel,
    rows,
    holdings,
    isLoading = false,
    onEdit,
}) => {
    const { t } = useTranslation('pages');

    const columns = React.useMemo<DataGridColumn<ShareholderRow>[]>(
        () => [
            {
                id: 'index',
                header: t('dividends.columns.index'),
                align: 'end',
                width: 56,
                cell: (row) => (
                    <span className="tabular-nums text-muted">{row.index}</span>
                ),
            },
            {
                id: 'name',
                header: t('dividends.columns.name'),
                isRowHeader: true,
                minWidth: 180,
                cell: (row) => (
                    <span className="font-medium text-foreground">
                        {row.name}
                    </span>
                ),
            },
            {
                id: 'identifier',
                header: t('dividends.columns.identifier'),
                minWidth: 140,
                cell: (row) => (
                    <span className="whitespace-nowrap tabular-nums">
                        {row.identifier}
                    </span>
                ),
            },
            {
                id: 'type',
                header: t('dividends.columns.type'),
                minWidth: 130,
                cell: (row) => (
                    <span className="whitespace-nowrap">{t(row.typeKey)}</span>
                ),
            },
            {
                id: 'residency',
                header: t('dividends.columns.residency'),
                minWidth: 130,
                cell: (row) => (
                    <span className="whitespace-nowrap">
                        {t(row.residencyKey)}
                    </span>
                ),
            },
            {
                id: 'holding',
                header: t('dividends.columns.holding'),
                align: 'end',
                minWidth: 120,
                cell: (row) => (
                    <span className="whitespace-nowrap font-medium tabular-nums text-foreground">
                        {row.holdingText}
                    </span>
                ),
            },
            {
                id: 'withholding',
                header: t('dividends.columns.withholding'),
                align: 'end',
                minWidth: 150,
                cell: (row) => (
                    <span className="flex flex-col items-end gap-0.5">
                        <span className="tabular-nums">
                            {row.withholdingText}
                        </span>
                        {row.isRateOverridden ? (
                            <span
                                className="inline-flex w-fit items-center rounded-lg bg-warning-soft px-2 py-0.5 text-xs font-medium text-warning-soft-foreground"
                                {...getNodeTestIdProp(
                                    `dividends-rate-override-${row.id}`,
                                )}
                            >
                                {t('dividends.rateOverridden')}
                            </span>
                        ) : null}
                    </span>
                ),
            },
            {
                id: 'cit',
                header: t('dividends.columns.cit'),
                minWidth: 130,
                cell: (row) =>
                    row.citExempt ? (
                        <span className="inline-flex w-fit items-center rounded-lg bg-surface-secondary px-2.5 py-1 text-xs font-medium text-muted">
                            {t('dividends.citExempt')}
                        </span>
                    ) : (
                        <span className="text-muted">
                            {t('dividends.citApplies')}
                        </span>
                    ),
            },
            {
                id: 'actions',
                header: (
                    <span className="sr-only">
                        {t('dividends.columns.actions')}
                    </span>
                ),
                align: 'end',
                width: 72,
                // The accessible name lives in a visually hidden span, not
                // `aria-label`: HeroUI's Tooltip wrapper drops an `aria-label`
                // set on its trigger Button, leaving an icon-only trigger
                // nameless.
                cell: (row) => (
                    <span
                        className="flex items-center justify-end"
                        data-row-actions=""
                    >
                        <Tooltip delay={0}>
                            <Button
                                isIconOnly
                                onPress={() => onEdit(row.id)}
                                size="sm"
                                variant="ghost"
                                {...getNodeTestIdProp(
                                    `dividends-shareholder-edit-${row.id}`,
                                )}
                            >
                                <Icon aria-hidden icon="gravity-ui:pencil" />
                                <span className="sr-only">
                                    {t('dividends.actions.editNamed', {
                                        name: row.name,
                                    })}
                                </span>
                            </Button>
                            <Tooltip.Content>
                                <p>{t('dividends.actions.edit')}</p>
                            </Tooltip.Content>
                        </Tooltip>
                    </span>
                ),
            },
        ],
        [onEdit, t],
    );

    if (!isLoading && rows.length === 0) {
        return (
            <div
                className="flex flex-col items-center gap-2 rounded-2xl border border-separator bg-surface px-6 py-14 text-center"
                {...getNodeTestIdProp('dividends-shareholders-empty')}
            >
                <Icon
                    aria-hidden
                    className="size-8 text-muted"
                    icon="gravity-ui:persons"
                />
                <span className="text-sm font-medium text-foreground">
                    {t('dividends.empty.noShareholders.title')}
                </span>
                <span className="max-w-sm text-sm text-muted">
                    {t('dividends.empty.noShareholders.body')}
                </span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {holdings && !holdings.isComplete ? (
                <div
                    className="flex flex-wrap items-center gap-2 rounded-xl bg-warning-soft px-3 py-2 text-sm text-warning-soft-foreground"
                    {...getNodeTestIdProp('dividends-holdings-warning')}
                >
                    <Icon aria-hidden icon="gravity-ui:circle-info" />
                    {t('dividends.holdingsWarning', {
                        total: holdings.totalText,
                        difference: holdings.differenceText,
                    })}
                </div>
            ) : null}

            {/* `contents` wrapper: `DataGrid` forwards no unknown props, so the
                register's testid has nowhere to land on the grid itself. */}
            <div
                className="contents"
                {...getNodeTestIdProp('dividends-shareholders-table')}
            >
                {isLoading ? (
                    <DataGrid<{ id: string }>
                        aria-label={ariaLabel}
                        className="ledger-data-grid"
                        columns={buildDataGridSkeletonColumns(columns)}
                        contentClassName="min-w-[900px]"
                        data={buildDataGridSkeletonRows(
                            SKELETON_ROW_COUNT,
                            'dividends-shareholders',
                        )}
                        getRowId={(row) => row.id}
                    />
                ) : (
                    <DataGrid<ShareholderRow>
                        aria-label={ariaLabel}
                        className="ledger-data-grid"
                        columns={columns}
                        contentClassName="min-w-[900px]"
                        data={rows}
                        getRowId={(row) => row.id}
                    />
                )}
            </div>
        </div>
    );
};
